// M6.1 — /app/utm-health. Same UTM scoring as the public snapshot at
// /utm-health-check but driven by the GA4 audit data (utmHealth) already
// computed server-side. We read from the cached /api/report payload so
// sessions are counted with real traffic weights rather than paste-only.
//
// The "Validate more URLs" panel at the bottom embeds the same
// <UtmBulkChecker> the public page uses — users can paste ad-hoc URLs
// without leaving the app (M6.4).

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { DataTable, type Column, type SortDirection } from "../components/ui/data-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { resolveIssueCopy } from "../components/utm/issue-copy";
import { ADVICE_BY_CATEGORY } from "../../shared/report/recommendations";
import { suggestCanonicalUrl } from "../../shared/utm/suggest";
import type { IssueCategory } from "../../shared/utm/types";
import { LoadingReport, ReportError as ReportErrorState } from "../components/report/ReportStates";
import { UtmBulkChecker } from "../components/utm/UtmBulkChecker";
import { cn } from "../lib/utils";
import { downloadXlsx } from "../lib/xlsx";
import { flaggedToCsv } from "../../shared/utm/score";
import type { UtmScoreResult } from "../../shared/utm/types";
import {
  fetchBootstrap,
  fetchReport,
  type BootstrapResponse,
  type ReportPayload,
  type ReportRange,
} from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

type Status =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "no-property" }
  | { kind: "ready"; bootstrap: BootstrapResponse; report: ReportPayload }
  | { kind: "error"; message: string };

const VALID_RANGES: ReadonlyArray<ReportRange> = ["last_7d", "last_28d", "last_90d"];

function isRange(v: string | null): v is ReportRange {
  return v !== null && (VALID_RANGES as readonly string[]).includes(v);
}

export function UtmHealthPage() {
  const [params] = useSearchParams();
  const range: ReportRange = isRange(params.get("range"))
    ? (params.get("range") as ReportRange)
    : "last_28d";

  const [status, setStatus] = useState<Status>({ kind: "loading" });
  // Hook must run on every render, BEFORE the early-return Navigate below
  // that the rules-of-hooks lint enforces. Title uses the propertyName
  // once it's loaded; shows a generic label while status === "loading".
  const propertyName =
    status.kind === "ready" ? status.bootstrap.defaultProperty?.propertyName : undefined;
  usePageMeta({
    title: propertyName
      ? `Tag health for ${propertyName} · Traffic Snapshot`
      : "Tag health · Traffic Snapshot",
  });

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: "loading" });
    (async () => {
      try {
        const bootstrap = await fetchBootstrap();
        if (cancelled) return;
        if (!bootstrap.authenticated) return setStatus({ kind: "unauthenticated" });
        if (!bootstrap.defaultProperty) return setStatus({ kind: "no-property" });
        const report = await fetchReport({
          propertyId: bootstrap.defaultProperty.propertyId,
          range,
          compare: "previous",
        });
        if (cancelled) return;
        setStatus({ kind: "ready", bootstrap, report });
      } catch (e) {
        if (cancelled) return;
        setStatus({ kind: "error", message: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  if (status.kind === "unauthenticated") {
    return <Navigate to="/app/connect?return_to=%2Fapp%2Futm-health" replace />;
  }
  const renderable: RenderableStatus = status;

  const property = renderable.kind === "ready" ? renderable.bootstrap.defaultProperty : null;

  return (
    <AppShell
      property={property ?? null}
      eyebrow="Tag health"
      title={
        <>
          How your <em className="font-serif text-purple-700">tags</em> are doing
        </>
      }
      subtitle={
        <>
          Scored against a canonical Sked Social taxonomy, weighted by real GA4 sessions.{" "}
          <Link to="/app/taxonomy" className="font-medium text-purple-700 hover:underline">
            What's canonical?
          </Link>
        </>
      }
    >
      {renderBody(renderable)}
    </AppShell>
  );
}

type RenderableStatus = Exclude<Status, { kind: "unauthenticated" }>;

function renderBody(status: RenderableStatus) {
  if (status.kind === "no-property") {
    return (
      <div className="space-y-6">
        <Alert>
          <i className="fa-regular fa-circle-info text-sm" aria-hidden="true" />
          <AlertTitle>Pick a property first</AlertTitle>
          <AlertDescription>
            Connect a GA4 property so we can score the UTMs on its social sessions.
          </AlertDescription>
        </Alert>
        <Button asChild>
          <Link to="/app/properties">
            Choose property
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    );
  }
  if (status.kind === "error") return <ReportErrorState message={status.message} />;
  if (status.kind === "loading") return <LoadingReport />;

  const propertyLabel = status.bootstrap.defaultProperty?.propertyName ?? "report";
  const utm = status.report.utmHealth;
  // Build a path → full URL map from the landingPages payload so the
  // flagged-rows "Landing page" column can show the hostname-prefixed URL
  // on hover even though the utm query itself can't pull hostName (GA4
  // caps nested requests at 9 unique dimensions across output + filter).
  const pathToFullUrl = new Map<string, string>();
  for (const lp of status.report.landingPages) {
    if (lp.pageUrl && !pathToFullUrl.has(lp.landingPage)) {
      pathToFullUrl.set(lp.landingPage, lp.pageUrl);
    }
  }

  return (
    <div className="space-y-10">
      <section id="score" className="scroll-mt-24">
        <ScoreCard score={utm} />
      </section>
      <section id="issues" className="scroll-mt-24 space-y-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">Issues</p>
          <span
            aria-hidden="true"
            className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500"
          />
          <h2 className="mt-3 font-display text-2xl font-semibold text-neutral-950">
            What to <em className="font-serif text-purple-700">fix</em>
          </h2>
        </div>
        <IssueList issues={utm.issues} />
      </section>
      <section id="flagged-urls" className="scroll-mt-24">
        <FlaggedRowsPanel utm={utm} propertyLabel={propertyLabel} pathToFullUrl={pathToFullUrl} />
      </section>
      <section id="validate" className="scroll-mt-24">
        <MorePanel />
      </section>
    </div>
  );
}

function ScoreCard({ score }: { score: UtmScoreResult }) {
  const bandColor: Record<UtmScoreResult["band"], string> = {
    excellent: "bg-green-500",
    good: "bg-green-400",
    "needs-work": "bg-yellow-500",
    broken: "bg-red-500",
  };
  const bandChipBg: Record<UtmScoreResult["band"], string> = {
    excellent: "bg-green-50 text-green-700",
    good: "bg-green-50 text-green-700",
    "needs-work": "bg-yellow-50 text-yellow-700",
    broken: "bg-red-50 text-red-700",
  };
  const bandLabel: Record<UtmScoreResult["band"], string> = {
    excellent: "Excellent",
    good: "Good",
    "needs-work": "Needs work",
    broken: "Broken",
  };

  return (
    <section className="rounded-2xl bg-white p-7 ring-1 ring-neutral-950/5">
      <div className="flex flex-wrap items-baseline justify-between gap-6">
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="What's in the audit?"
                className="inline-flex items-center gap-1.5 rounded font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                {score.totals.rows.toLocaleString()} tagged URLs ·{" "}
                {score.totals.sessions.toLocaleString()} sessions
                <i className="fa-regular fa-circle-info text-[10px]" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              <div className="font-semibold">Tagged URLs</div>
              <div className="mt-1">
                A unique combination of source / medium / campaign / landing page GA4 saw this
                period. Two visits to the same page via two different campaigns count as two tagged
                URLs.
              </div>
              <div className="mt-2 font-semibold">Sessions</div>
              <div className="mt-1">
                Each time a visitor arrived through one of these tagged URLs. This is the weight we
                use when scoring tagging quality. More sessions on a broken URL hurts the score more
                than fewer sessions on the same problem.
              </div>
            </TooltipContent>
          </Tooltip>
          <div>
            <span
              className={cn(
                "mt-2 inline-flex rounded-full px-2.5 py-1.5 text-[13px] font-bold",
                bandChipBg[score.band],
              )}
            >
              {bandLabel[score.band]}
            </span>
          </div>
        </div>
        <p className="font-display text-[72px] font-[900] leading-none tracking-[-0.03em] text-neutral-950 tabular-nums">
          {score.score}
          <span className="ml-1 text-2xl font-semibold text-neutral-400">/100</span>
        </p>
      </div>
      <div aria-hidden="true" className="mt-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <div
          className={`h-full rounded-full ${bandColor[score.band]}`}
          style={{ width: `${score.score}%` }}
        />
      </div>
    </section>
  );
}

// Severity still drives the dot + the issue's priority label — that's
// the "how urgent is this" signal. The pill/chip color now encodes the
// issue *category* instead, so a glance at the list distinguishes
// "missing params" from "bad naming" from "attribution leak" even
// when two issues share the same severity band.
const SEVERITY_TONE: Record<
  UtmScoreResult["issues"][number]["severity"],
  { dot: string; label: string }
> = {
  severe: { dot: "bg-red-500", label: "Fix first" },
  medium: { dot: "bg-amber-500", label: "Worth a look" },
  mild: { dot: "bg-neutral-300", label: "Nudge" },
};

// Warm-spectrum palette with enough hue separation that the six
// categories are distinguishable at a glance without drifting out of
// the "warning" read. required-params is the most severe-feeling
// (red) because a missing param blocks attribution entirely; the
// rest fan out through amber → orange → yellow and wrap into
// rose/fuchsia for the attribution-adjacent categories.
const CATEGORY_TONE: Record<UtmScoreResult["issues"][number]["category"], string> = {
  "required-params": "bg-red-50 text-red-700 ring-red-200",
  "canonical-naming": "bg-amber-50 text-amber-700 ring-amber-200",
  "campaign-structure": "bg-orange-50 text-orange-700 ring-orange-200",
  "platform-metadata": "bg-yellow-50 text-yellow-800 ring-yellow-200",
  "content-granularity": "bg-rose-50 text-rose-700 ring-rose-200",
  "dark-social-leakage": "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  formatting: "bg-sky-50 text-sky-700 ring-sky-200",
  vocabulary: "bg-violet-50 text-violet-700 ring-violet-200",
  "anti-pattern": "bg-pink-50 text-pink-700 ring-pink-200",
};

function prettyCategory(cat: UtmScoreResult["issues"][number]["category"]): string {
  switch (cat) {
    case "required-params":
      return "Required params";
    case "canonical-naming":
      return "Canonical naming";
    case "campaign-structure":
      return "Campaign structure";
    case "platform-metadata":
      return "Platform metadata";
    case "content-granularity":
      return "Content granularity";
    case "dark-social-leakage":
      return "Dark social leakage";
    case "formatting":
      return "Formatting";
    case "vocabulary":
      return "Vocabulary";
    case "anti-pattern":
      return "Anti-pattern";
    default:
      return cat;
  }
}

function IssueList({ issues }: { issues: UtmScoreResult["issues"] }) {
  // Default to showing the first 4; the rest sit behind "Show more".
  // Severe issues always render so they can't be hidden under the fold.
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_VISIBLE = 4;

  if (issues.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-7 ring-1 ring-neutral-950/5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700 ring-1 ring-green-100">
            <i className="fa-regular fa-check text-sm" aria-hidden="true" />
          </span>
          <div>
            <p className="font-display text-base font-semibold text-neutral-950">
              Nothing flagged this period
            </p>
            <p className="mt-1 text-sm text-neutral-600 text-pretty">
              Tagging is canonical, required params are present, and mediums look clean.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const visible = showAll ? issues : issues.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = issues.length - visible.length;

  return (
    <div className="space-y-3">
      <ul role="list" className="grid gap-3">
        {visible.map((issue) => {
          const severity = SEVERITY_TONE[issue.severity];
          const categoryChip = CATEGORY_TONE[issue.category];
          const builderHref =
            issue.category === "canonical-naming"
              ? "/app/builder?medium=organic-social"
              : issue.category === "required-params"
                ? "/app/builder"
                : null;
          return (
            <li
              key={`${issue.category}-${issue.label}`}
              className="flex items-start gap-3 rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5"
            >
              <span
                aria-hidden="true"
                className={cn("mt-1.5 size-2 shrink-0 rounded-full", severity.dot)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      // SKE-3432 — sentence case + warning glyph to match
                      // the per-row "What's wrong" chip in the flagged
                      // table below. Two amber chips that look identical
                      // read as one consistent "issue category" primitive.
                      "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                      categoryChip,
                    )}
                  >
                    <i
                      className="fa-regular fa-triangle-exclamation text-[10px]"
                      aria-hidden="true"
                    />
                    {prettyCategory(issue.category)}
                  </span>
                  <span className="text-sm font-semibold text-neutral-900">{issue.label}</span>
                  <span className="ml-auto text-xs text-neutral-500 tabular-nums">
                    {issue.rowCount.toLocaleString()} row
                    {issue.rowCount === 1 ? "" : "s"} · {issue.sessionCount.toLocaleString()}{" "}
                    sessions
                  </span>
                </div>
                <p className="mt-2 text-sm text-neutral-700 text-pretty">{issue.detail}</p>
                {issue.examples.length > 0 ? (
                  <p className="mt-2 truncate font-mono text-xs text-neutral-500">
                    e.g. {issue.examples.slice(0, 2).join(" · ")}
                  </p>
                ) : null}
                {builderHref ? (
                  <Button variant="ghost" size="sm" asChild className="mt-3 text-purple-700">
                    <Link to={builderHref}>
                      Open UTM builder
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {issues.length > DEFAULT_VISIBLE ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowAll((prev) => !prev)}
            className="text-neutral-700"
          >
            {showAll ? (
              <>
                Show fewer
                <i className="fa-solid fa-chevron-up text-xs" aria-hidden="true" />
              </>
            ) : (
              <>
                Show {hiddenCount} more {hiddenCount === 1 ? "issue" : "issues"}
                <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// The flagged[].issues array holds `${category}:${key}` strings from the
// scorer. Strip the key, keep unique categories so the UI shows one
// human-readable recommendation per category rather than the internal
// "canonical-naming:non-canonical-source" tag.
function uniqueCategoriesForRow(row: UtmScoreResult["flagged"][number]): IssueCategory[] {
  const seen = new Set<string>();
  const out: IssueCategory[] = [];
  for (const raw of row.issues) {
    const cat = raw.split(":")[0] as IssueCategory;
    if (seen.has(cat)) continue;
    seen.add(cat);
    out.push(cat);
  }
  return out;
}

type FlaggedPageSize = 5 | 15 | 25 | 50 | 100 | "all";
const FLAGGED_PAGE_SIZES: FlaggedPageSize[] = [5, 15, 25, 50, 100, "all"];
const FLAGGED_DEFAULT_PAGE_SIZE: FlaggedPageSize = 5;

function FlaggedRowsPanel({
  utm,
  propertyLabel,
  pathToFullUrl,
}: {
  utm: UtmScoreResult;
  propertyLabel: string;
  pathToFullUrl: Map<string, string>;
}) {
  // useMemo so the `?? []` fallback doesn't produce a fresh reference
  // every render and invalidate the sort/slice memos downstream.
  const flagged = useMemo(() => utm.flagged ?? [], [utm.flagged]);
  const [sortColumn, setSortColumn] = useState<string>("sessions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [pageSize, setPageSize] = useState<FlaggedPageSize>(FLAGGED_DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);

  function sortCmp(
    a: UtmScoreResult["flagged"][number],
    b: UtmScoreResult["flagged"][number],
    col: string,
    dir: SortDirection,
  ): number {
    if (!dir) return 0;
    const d = dir === "asc" ? 1 : -1;
    if (col === "sessions") return ((a.sessions ?? 0) - (b.sessions ?? 0)) * d;
    const av = (a as unknown as Record<string, string | undefined>)[col] ?? "";
    const bv = (b as unknown as Record<string, string | undefined>)[col] ?? "";
    return av.localeCompare(bv) * d;
  }

  const sortedRows = useMemo(() => {
    if (!sortDirection) return flagged;
    return [...flagged].sort((a, b) => sortCmp(a, b, sortColumn, sortDirection));
  }, [flagged, sortColumn, sortDirection]);

  const totalRows = sortedRows.length;
  const effectivePageSize = pageSize === "all" ? totalRows : pageSize;
  const pageCount = Math.max(1, Math.ceil(totalRows / (effectivePageSize || 1)));
  const safePage = Math.min(currentPage, pageCount - 1);
  const sliceStart = effectivePageSize ? safePage * effectivePageSize : 0;
  const sliceEnd = effectivePageSize ? sliceStart + effectivePageSize : totalRows;
  const pagedRows = useMemo(
    () => sortedRows.slice(sliceStart, sliceEnd),
    [sortedRows, sliceStart, sliceEnd],
  );
  const visibleFrom = totalRows === 0 ? 0 : sliceStart + 1;
  const visibleTo = Math.min(totalRows, sliceEnd);

  const columns = useMemo<Column<UtmScoreResult["flagged"][number]>[]>(
    () => [
      {
        id: "source",
        header: "Source",
        sortable: true,
        cell: (row) => (
          <span className="font-mono text-xs text-neutral-900">{row.source ?? "—"}</span>
        ),
      },
      {
        id: "medium",
        header: "Medium",
        sortable: true,
        cell: (row) => (
          <span className="font-mono text-xs text-neutral-900">{row.medium ?? "—"}</span>
        ),
      },
      {
        id: "campaign",
        header: "Campaign",
        sortable: true,
        cell: (row) => (
          <span className="font-mono text-xs text-neutral-900">{row.campaign ?? "—"}</span>
        ),
      },
      {
        id: "url",
        header: "Landing page",
        sortable: true,
        cell: (row) => {
          if (!row.url) return <span className="font-mono text-xs text-neutral-400">—</span>;
          // Prefer a full URL from the landingPages map (path → host + path +
          // query). Fall back to the flagged row's own value (path) when the
          // path wasn't in the top-250 landingPages window.
          const fullUrl = row.url.startsWith("http")
            ? row.url
            : (pathToFullUrl.get(row.url) ?? null);
          const display = row.url; // always show the path in the cell for brevity
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                {fullUrl ? (
                  <a
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block max-w-[20rem] truncate font-mono text-xs text-neutral-900 underline decoration-transparent underline-offset-2 hover:decoration-purple-500 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
                  >
                    {display}
                  </a>
                ) : (
                  <span className="block max-w-[20rem] truncate font-mono text-xs text-neutral-900">
                    {display}
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-md">
                <div className="font-mono text-[11px] break-all">{fullUrl ?? display}</div>
                {fullUrl ? (
                  <div className="mt-1 text-[10px] text-white/60">Click to open in a new tab</div>
                ) : (
                  <div className="mt-1 text-[10px] text-white/60">
                    Full URL unavailable. This page isn't in the top-250 landing pages window.
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "sessions",
        header: "Sessions",
        sortable: true,
        cell: (row) => <span className="tabular-nums">{(row.sessions ?? 0).toLocaleString()}</span>,
      },
      {
        id: "issues",
        header: "What's wrong",
        sortable: false,
        cell: (row) => {
          return (
            <div className="flex max-w-[28rem] flex-wrap gap-2">
              {row.issues.map((issueStr) => {
                const { copy, category, key } = resolveIssueCopy(issueStr);
                return (
                  <Tooltip key={issueStr}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={`${copy.pill}: details`}
                        className={
                          "inline-flex items-center gap-1.5 rounded-full py-1 pr-2.5 pl-2 text-[11px] font-semibold whitespace-nowrap ring-1 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2 " +
                          copy.toneClass
                        }
                      >
                        <i className={`${copy.icon} text-[10px]`} aria-hidden="true" />
                        {copy.pill}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-white/70">
                        {category}
                        {key ? ` · ${key}` : ""}
                      </div>
                      <div className="mt-1">
                        {ADVICE_BY_CATEGORY[category] ?? "See the UTM builder for canonical shape."}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          );
        },
      },
    ],
    [pathToFullUrl],
  );
  if (flagged.length === 0) return null;

  const slug = propertyLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const filenameBase = `utm-flagged-${slug || "report"}-${new Date().toISOString().slice(0, 10)}`;

  function downloadCsv() {
    const blob = new Blob([flaggedToCsv(flagged)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenameBase}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadXlsxFile() {
    // Sheet 1: "Fix these" — flagged rows with original URL and a suggested
    // improved URL. Sheet 2: "All flagged rows" — the same set stripped of
    // the suggestion column for copy-paste into other tools. The full
    // audited row set (scored + passing) isn't in the client payload.
    const flaggedRows = flagged.map((r) => {
      const cats = uniqueCategoriesForRow(r);
      const advice = cats
        .map((c) => `${c}: ${ADVICE_BY_CATEGORY[c] ?? "See the UTM builder for canonical shape."}`)
        .join(" | ");
      return [
        r.url ?? "",
        suggestCanonicalUrl(r) ?? "",
        r.source ?? "",
        r.medium ?? "",
        r.campaign ?? "",
        r.content ?? "",
        r.term ?? "",
        r.sourcePlatform ?? "",
        r.sessions ?? 0,
        advice,
      ];
    });
    const allRows = flagged.map((r) => [
      r.url ?? "",
      r.source ?? "",
      r.medium ?? "",
      r.campaign ?? "",
      r.content ?? "",
      r.term ?? "",
      r.sourcePlatform ?? "",
      r.sessions ?? 0,
      uniqueCategoriesForRow(r).join(", "),
    ]);

    await downloadXlsx({
      fileName: `${filenameBase}.xlsx`,
      sheets: [
        {
          sheetName: "Fix these",
          columns: [
            "Original URL",
            "Suggested URL",
            "Source",
            "Medium",
            "Campaign",
            "Content",
            "Term",
            "Source platform",
            "Sessions",
            "How to fix",
          ],
          rows: flaggedRows,
        },
        {
          sheetName: "All flagged rows",
          columns: [
            "URL",
            "Source",
            "Medium",
            "Campaign",
            "Content",
            "Term",
            "Source platform",
            "Sessions",
            "Issue categories",
          ],
          rows: allRows,
        },
      ],
    });
  }

  return (
    <section className="space-y-3">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
          Flagged rows
        </p>
        <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-semibold text-neutral-950">
              {flagged.length.toLocaleString()} tagged URL{flagged.length === 1 ? "" : "s"} need
              {flagged.length === 1 ? "s" : ""}{" "}
              <em className="font-serif text-purple-700">attention</em>
            </h2>
            <p className="mt-1 max-w-[60ch] text-xs text-neutral-500 text-pretty">
              Each row is a unique combination of source, medium, campaign and landing page GA4 saw
              this period.
            </p>
            <p className="mt-2.5 max-w-[60ch] text-xs text-neutral-500 text-pretty">
              Compared against the canonical Sked Social taxonomy.{" "}
              <Link
                to="/app/taxonomy"
                className="font-medium text-purple-700 hover:text-purple-900 hover:underline"
              >
                What counts as canonical?
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadXlsxFile}
              className="text-purple-700"
            >
              <i className="fa-regular fa-file-excel text-sm" aria-hidden="true" />
              Excel
              <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
            </Button>
            <Button variant="ghost" size="sm" onClick={downloadCsv} className="text-neutral-600">
              <i className="fa-regular fa-file-csv text-sm" aria-hidden="true" />
              CSV
              <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
      <DataTable
        key={`${sortColumn}-${sortDirection ?? "none"}`}
        columns={columns}
        data={pagedRows}
        getRowId={rowKey}
        defaultSortColumn={sortColumn}
        defaultSortDirection={sortDirection}
        onSortChange={(col, dir) => {
          setSortColumn(col);
          setSortDirection(dir);
          setCurrentPage(0);
        }}
        sortComparator={sortCmp}
      />
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-600">
        <label className="flex items-center gap-2">
          <span className="text-neutral-500">Rows per page</span>
          <select
            value={pageSize === "all" ? "all" : String(pageSize)}
            onChange={(e) => {
              const v = e.target.value;
              setPageSize(v === "all" ? "all" : (Number(v) as FlaggedPageSize));
              setCurrentPage(0);
            }}
            className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
          >
            {FLAGGED_PAGE_SIZES.map((size) => (
              <option key={String(size)} value={String(size)}>
                {size === "all" ? "All" : size}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-3">
          <span className="tabular-nums text-neutral-500">
            {visibleFrom.toLocaleString()} to {visibleTo.toLocaleString()} of{" "}
            {totalRows.toLocaleString()}
          </span>
          <nav className="flex items-center gap-1" aria-label="Flagged rows pagination">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
              disabled={pageCount <= 1 || safePage === 0}
              aria-label="Previous page"
              className="flex size-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 transition-colors hover:border-neutral-300 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:text-neutral-600"
            >
              <i className="fa-regular fa-chevron-left text-xs" aria-hidden="true" />
            </button>
            <span className="tabular-nums px-2 text-neutral-700 max-sm:hidden">
              Page {safePage + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={pageCount <= 1 || safePage >= pageCount - 1}
              aria-label="Next page"
              className="flex size-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 transition-colors hover:border-neutral-300 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:text-neutral-600"
            >
              <i className="fa-regular fa-chevron-right text-xs" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </section>
  );
}

function rowKey(r: UtmScoreResult["flagged"][number]): string {
  return `${r.source ?? ""}::${r.medium ?? ""}::${r.campaign ?? ""}::${r.url ?? ""}`;
}

function MorePanel() {
  return (
    <section className="space-y-3">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
          Validate more URLs
        </p>
        <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
      </div>
      <details className="group rounded-2xl bg-white ring-1 ring-neutral-950/5">
        <summary className="flex cursor-pointer select-none list-none flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-4 hover:bg-neutral-50 [&::-webkit-details-marker]:hidden">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-neutral-950">
              Paste or upload to <em className="font-serif text-purple-700">check</em> a link
            </h2>
            <p className="mt-1 max-w-[60ch] text-sm text-neutral-600">
              Ad-hoc links score against the same taxonomy. Nothing is persisted.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 text-xs font-medium text-neutral-600">
            <span className="group-open:hidden">Open validator</span>
            <span className="hidden group-open:inline">Hide validator</span>
            <i
              className="fa-solid fa-chevron-down text-xs transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </span>
        </summary>
        <div className="border-t border-neutral-200 p-5">
          <UtmBulkChecker variant="app" />
        </div>
      </details>
    </section>
  );
}
